import { z } from "zod";

import { PaginationParamsSchema } from "./common.schemas";

// ============================================================================
// VERSION TYPE
// ============================================================================

export const VersionTypeSchema = z.enum(["AUTO", "CHECKPOINT"]);

// ============================================================================
// VERSION SUMMARY (for list)
// ============================================================================

export const VersionSummarySchema = z.object({
  id: z.string().uuid(),
  type: VersionTypeSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  isProduction: z.boolean(),
  createdAt: z.coerce.date(),
  createdBy: z.string().nullable(),
  creator: z
    .object({
      id: z.string(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable()
    })
    .nullable()
});

// ============================================================================
// VERSION DETAIL (for get endpoint - includes prompt)
// ============================================================================

export const VersionDetailSchema = VersionSummarySchema.extend({
  prompt: z.string().nullable()
});

// ============================================================================
// LIST VERSIONS
// ============================================================================

export const ListVersionsParamsSchema = PaginationParamsSchema.extend({
  type: VersionTypeSchema.optional()
});

export const ListVersionsResponseSchema = z.object({
  versions: z.array(VersionSummarySchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
    checkpointCount: z.number().int()
  })
});

// ============================================================================
// GET VERSION
// ============================================================================

export const GetVersionResponseSchema = VersionDetailSchema;

// ============================================================================
// CREATE CHECKPOINT
// ============================================================================

// The internal dashboard imposes no prompt-length limit, the DB stores the
// prompt as JSONB, and the request body parser allows up to 10mb. The public
// API previously capped prompts at 100k chars, which blocked read-modify-write
// of agents whose stored prompts already exceed that (e.g. 400k). This cap is a
// generous safety bound (~1mb of text) that stays comfortably under the body
// limit while accepting any prompt the platform itself stores.
export const MAX_PROMPT_LENGTH = 1_000_000;

export const CreateCheckpointBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().optional(),
  prompt: z.string().max(MAX_PROMPT_LENGTH).optional(),
  autoPublish: z.boolean().optional()
});

export const CreateCheckpointResponseSchema = VersionDetailSchema;

// ============================================================================
// UPDATE VERSION
// ============================================================================

export const UpdateVersionBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().optional()
});

export const UpdateVersionResponseSchema = VersionDetailSchema;

// ============================================================================
// DELETE VERSION
// ============================================================================

export const DeleteVersionResponseSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true)
});

// ============================================================================
// RESTORE VERSION
// ============================================================================

export const RestoreVersionResponseSchema = z.object({
  prompt: z.string().nullable(),
  message: z.string()
});

// ============================================================================
// PUBLISH VERSION
// ============================================================================

export const PublishVersionResponseSchema = VersionDetailSchema;
